import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
    Bell, BellRinging, ArrowLeft, Tag, Package, Trash,
    Check, ArrowDown, ArrowsClockwise
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const getH = () => ({});

const TYPE_CONFIG = {
    price_drop: { label: 'Price Drop', icon: ArrowDown, color: 'text-neon-green', bg: 'bg-neon-green/10' },
    restock: { label: 'Back in Stock', icon: Package, color: 'text-electric-blue', bg: 'bg-electric-blue/10' },
    any_change: { label: 'Any Update', icon: ArrowsClockwise, color: 'text-boltz-gold', bg: 'bg-boltz-gold/10' },
};

export default function PriceAlerts() {
    const navigate = useNavigate();
    const [alerts, setAlerts] = useState([]);
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState('alerts');

    useEffect(() => {
        Promise.all([
            fetch(`${API}/alerts`, { credentials: 'include', headers: getH() }).then(r => r.ok ? r.json() : []),
            fetch(`${API}/alerts/notifications`, { credentials: 'include', headers: getH() }).then(r => r.ok ? r.json() : []),
        ]).then(([a, n]) => { setAlerts(a); setNotifications(n); })
          .finally(() => setLoading(false));
    }, []);

    const deleteAlert = async (alertId) => {
        try {
            const res = await fetch(`${API}/alerts/${alertId}`, {
                method: 'DELETE', credentials: 'include', headers: getH(),
            });
            if (res.ok) {
                setAlerts(prev => prev.filter(a => a.alert_id !== alertId));
                toast.success('Alert removed');
            }
        } catch { toast.error('Failed'); }
    };

    const markRead = async (notifId) => {
        try {
            await fetch(`${API}/alerts/notifications/${notifId}/read`, {
                method: 'POST', credentials: 'include', headers: getH(),
            });
            setNotifications(prev => prev.map(n =>
                n.notification_id === notifId ? { ...n, is_read: true } : n
            ));
        } catch {}
    };

    const unreadCount = notifications.filter(n => !n.is_read).length;

    return (
        <Layout>
            <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
                    <Button size="sm" variant="ghost" onClick={() => navigate('/shop')} className="mb-2 text-muted-foreground">
                        <ArrowLeft size={16} className="mr-1" />Shop
                    </Button>
                    <span className="label-text text-electric-blue text-glow-blue">ALERTS</span>
                    <h1 className="text-2xl sm:text-3xl font-bold mt-1 font-display text-foreground" data-testid="alerts-title">
                        Price Alerts
                    </h1>
                    <p className="text-muted-foreground text-sm mt-1">Get notified when prices drop or items restock</p>
                </motion.div>

                {/* Tabs */}
                <div className="flex gap-2 mb-6">
                    <Button size="sm" variant={tab === 'alerts' ? 'default' : 'outline'}
                        onClick={() => setTab('alerts')}
                        className={`rounded-full ${tab === 'alerts' ? 'btn-primary' : ''}`}>
                        <Bell size={14} className="mr-1" />Active ({alerts.filter(a => a.is_active).length})
                    </Button>
                    <Button size="sm" variant={tab === 'notifications' ? 'default' : 'outline'}
                        onClick={() => setTab('notifications')}
                        className={`rounded-full ${tab === 'notifications' ? 'btn-primary' : ''}`}
                        data-testid="notifications-tab">
                        <BellRinging size={14} className="mr-1" />
                        Notifications
                        {unreadCount > 0 && (
                            <Badge className="ml-1 bg-plasma-red text-white text-[9px] px-1.5 py-0">{unreadCount}</Badge>
                        )}
                    </Button>
                </div>

                {tab === 'alerts' ? (
                    <div className="space-y-3">
                        {alerts.length === 0 ? (
                            <Card className="glass-card">
                                <CardContent className="p-8 text-center">
                                    <Bell size={48} className="text-muted-foreground mx-auto mb-4" />
                                    <p className="font-medium text-foreground">No price alerts</p>
                                    <p className="text-sm text-muted-foreground mt-1">
                                        Set alerts from any product page to get notified of price changes
                                    </p>
                                    <Button onClick={() => navigate('/shop')} className="mt-4 btn-primary">Browse Shop</Button>
                                </CardContent>
                            </Card>
                        ) : (
                            <AnimatePresence>
                                {alerts.map(alert => {
                                    const cfg = TYPE_CONFIG[alert.type] || TYPE_CONFIG.any_change;
                                    const Icon = cfg.icon;
                                    return (
                                        <motion.div key={alert.alert_id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, x: -100 }}>
                                            <Card className="glass-card" data-testid={`alert-${alert.alert_id}`}>
                                                <CardContent className="p-3 flex items-center gap-3">
                                                    <div className={`w-10 h-10 rounded-xl ${cfg.bg} flex items-center justify-center shrink-0`}>
                                                        <Icon size={18} className={cfg.color} />
                                                    </div>
                                                    <div className="flex-1 min-w-0 cursor-pointer" onClick={() => navigate(`/shop/item/${alert.item_id}`)}>
                                                        <p className="font-bold text-sm text-foreground truncate">{alert.item_name}</p>
                                                        <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                                                            <Badge variant="outline" className={`text-[9px] ${cfg.color} border-current`}>{cfg.label}</Badge>
                                                            {alert.target_price && <span className="font-mono">Target: {alert.target_price} Boltz</span>}
                                                            {!alert.is_active && <span className="text-neon-green font-bold">Triggered</span>}
                                                        </div>
                                                    </div>
                                                    <Button size="sm" variant="ghost"
                                                        onClick={() => deleteAlert(alert.alert_id)}
                                                        className="text-muted-foreground hover:text-plasma-red shrink-0">
                                                        <Trash size={16} />
                                                    </Button>
                                                </CardContent>
                                            </Card>
                                        </motion.div>
                                    );
                                })}
                            </AnimatePresence>
                        )}
                    </div>
                ) : (
                    <div className="space-y-3">
                        {notifications.length === 0 ? (
                            <Card className="glass-card">
                                <CardContent className="p-8 text-center">
                                    <BellRinging size={48} className="text-muted-foreground mx-auto mb-4" />
                                    <p className="font-medium text-foreground">No notifications yet</p>
                                    <p className="text-sm text-muted-foreground mt-1">
                                        Triggered alerts will appear here
                                    </p>
                                </CardContent>
                            </Card>
                        ) : (
                            notifications.map(notif => (
                                <Card key={notif.notification_id}
                                    className={`glass-card cursor-pointer ${notif.is_read ? 'opacity-60' : 'border-primary/20'}`}
                                    onClick={() => { markRead(notif.notification_id); navigate(`/shop/item/${notif.item_id}`); }}
                                    data-testid={`notif-${notif.notification_id}`}>
                                    <CardContent className="p-3 flex items-center gap-3">
                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                                            notif.is_read ? 'bg-muted' : 'bg-primary/10'
                                        }`}>
                                            {notif.is_read ? <Check size={14} className="text-muted-foreground" /> : <BellRinging size={14} className="text-primary" />}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className={`text-sm ${notif.is_read ? 'text-muted-foreground' : 'text-foreground font-medium'}`}>
                                                {notif.message}
                                            </p>
                                            <p className="text-[10px] text-muted-foreground">{new Date(notif.created_at).toLocaleString()}</p>
                                        </div>
                                    </CardContent>
                                </Card>
                            ))
                        )}
                    </div>
                )}
            </div>
        </Layout>
    );
}
